import { posAPI, purchasesAPI, khataAPI } from '@/lib/api';
import {
  putProducts,
  replaceAllProducts,
  putCustomers,
  putSalesCache,
  getPendingQueueItems,
  updateQueueItem,
  setProductsStoreId,
} from '@/lib/offlineDB';

let _productsDirty = false;
export const markProductsDirty = () => { _productsDirty = true; };
export const isProductsDirty    = () => _productsDirty;
export const markProductsClean  = () => { _productsDirty = false; };

// Shared "when did the full catalog last successfully sync" timestamp. Stock
// counts are kept current via the stock_changed Realtime broadcast, so this
// is only about the rarely-changing catalog fields (name/price/gst/etc.) —
// safe to check with a long cooldown before forcing another full re-fetch.
const _PRODUCTS_SYNC_KEY = 'kiranaflow_products_sync_ts';

export function isProductSyncStale(cooldownMs) {
  const last = localStorage.getItem(_PRODUCTS_SYNC_KEY);
  if (!last) return true;
  return Date.now() - parseInt(last, 10) > cooldownMs;
}

export function markProductsSynced() {
  localStorage.setItem(_PRODUCTS_SYNC_KEY, String(Date.now()));
}

// Content fingerprint of the last catalog payload actually written to
// IndexedDB. Lets syncProducts() skip the clear+rewrite (the expensive part)
// when the freshly-fetched catalog is byte-identical to what's already
// stored — e.g. when the backend's own pos_products cache (30-min TTL)
// serves the same response across several consecutive 5-min polls. The
// network fetch itself always still happens, unchanged.
const _PRODUCTS_HASH_KEY = 'kiranaflow_products_hash';

async function _hashProducts(items) {
  // crypto.subtle requires a secure context; if it's ever unavailable, return
  // null so the caller falls back to always writing — same as before this change.
  if (!window.crypto?.subtle) return null;
  try {
    const sorted = [...items].sort((a, b) => (a.catalog_id || '').localeCompare(b.catalog_id || ''));
    const bytes = new TextEncoder().encode(JSON.stringify(sorted));
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

// Marks which {count, last_modified} snapshot of product_catalog the last full sync
// actually wrote to IndexedDB. Compared against GET /pos/products/version (a couple of
// bytes) before paying for the full ~11k-row transfer — most of the time nothing has
// changed since the last sync (Realtime broadcast already keeps open POS/StockMaster
// tabs current), so this turns most periodic/boot syncs into one cheap query instead of
// a full catalog re-fetch.
const _CATALOG_VERSION_KEY = 'kiranaflow_catalog_version';

export async function syncProducts(storeId) {
  let versionStr = null;
  try {
    const v = await posAPI.getProductsVersion();
    const { count, last_modified } = v.data ?? {};
    versionStr = `${count}|${last_modified}`;
    if (localStorage.getItem(_CATALOG_VERSION_KEY) === versionStr) {
      markProductsSynced();
      return 0; // nothing changed — skip the full transfer entirely
    }
  } catch {
    // Version check failed (network hiccup, etc.) — fall through to the full fetch,
    // same behavior as before this optimization existed.
  }

  const r = await posAPI.getProducts();
  const items = r.data?.items ?? [];
  if (items.length) {
    const hash = await _hashProducts(items);
    if (hash === null || hash !== localStorage.getItem(_PRODUCTS_HASH_KEY)) {
      await replaceAllProducts(items);
      if (hash !== null) localStorage.setItem(_PRODUCTS_HASH_KEY, hash);
    }
  } else {
    await replaceAllProducts(items); // no-op for empty payloads, same as before
  }
  // Only recorded once the full fetch actually succeeded, so a failed getProducts()
  // call above doesn't leave us thinking we're caught up when we're not.
  if (versionStr !== null) localStorage.setItem(_CATALOG_VERSION_KEY, versionStr);
  if (storeId) setProductsStoreId(storeId);
  markProductsSynced();
  return items.length;
}

export async function syncCustomers() {
  const r = await posAPI.listCustomers({ limit: 500 });
  const items = r.data?.items ?? [];
  await putCustomers(items);
  return items.length;
}

export async function syncSalesCache(startDate, endDate) {
  const r = await posAPI.listSales({
    start_date: startDate,
    end_date: endDate,
    include_deleted: true,
    limit: 500,
  });
  const sales = r.data?.sales ?? [];
  await putSalesCache(sales);
  return sales.length;
}

// Processes pending queue items sequentially to preserve bill-number ordering.
export async function flushOfflineQueue(onProgress) {
  const pending = await getPendingQueueItems();
  if (pending.length === 0) return 0;

  let processed = 0;
  for (const item of pending) {
    await updateQueueItem(item.local_id, { status: 'syncing' });
    try {
      let payload = { ...item.payload };

      // Resolve temp products → POST /purchases → substitute real catalog_ids
      if (item.temp_products?.length) {
        const today = new Date().toISOString().split('T')[0];
        const res = await purchasesAPI.create({
          purchase_date: today,
          items: item.temp_products.map(tp => ({
            product_name:     tp.product_name,
            barcode:          tp.barcode || undefined,
            qty:              tp.qty,
            cost_price:       tp.cost_price ?? 0,
            mrp:    tp.mrp ?? 0,
            default_discount: tp.default_discount || undefined,
          })),
        });
        const resolvedItems = res.data?.items ?? [];
        const idMap = {};
        item.temp_products.forEach((tp, i) => {
          if (resolvedItems[i]?.catalog_id) idMap[tp.temp_id] = resolvedItems[i].catalog_id;
        });
        payload = {
          ...payload,
          items: payload.items.map(si =>
            idMap[si.catalog_id] ? { ...si, catalog_id: idMap[si.catalog_id] } : si
          ),
        };
      }

      // Resolve temp customer → POST /customers → substitute real customer_id
      if (item.temp_customer) {
        const res = await posAPI.createCustomer({
          name:   item.temp_customer.name,
          mobile: item.temp_customer.mobile,
        });
        payload = { ...payload, customer_id: res.data.id };
      }

      const r = await posAPI.createSale(payload);
      const real_bill_number = r.data.bill_number;
      const sale = r.data.sale;

      // If outstanding khata was collected offline, apply the payment now that we're back online.
      if (payload.khata_cleared > 0 && payload.customer_id) {
        try {
          await khataAPI.collect({
            customer_id: payload.customer_id,
            amount:      payload.khata_cleared,
            note:        'Collected at POS (offline sync)',
            bill_number: real_bill_number,
          });
        } catch {} // best-effort; can be reconciled from Customer Insights
      }

      await updateQueueItem(item.local_id, {
        status: 'synced',
        real_bill_number,
        error: null,
      });
      if (sale) {
        await putSalesCache([sale]);
      }
    } catch (err) {
      let errMsg = err?.response?.data?.detail || err?.message || 'Unknown error';
      if (errMsg.includes('Unknown products') && payload.items?.length) {
        const names = payload.items.map(i => i.product_name || i.catalog_id).join(', ');
        errMsg = `Products no longer in catalog: ${names}. Please re-enter this bill manually.`;
      }
      await updateQueueItem(item.local_id, { status: 'failed', error: errMsg });
    }
    processed++;
    onProgress?.({ processed, total: pending.length, item });
  }
  return processed;
}
